import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as AutomixIcon } from '../../assets/icons/categories/automix.svg';
import AddToPlaylistModal from '../AddToPlaylistModal';
import TabFilterBox from '../TabFilterBox';
import { useToast } from '../../contexts/ToastContext';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { selectAutomixSource, openAutomixSet } from '../../redux/thunk/automixThunk';
import { setSetsExpanded } from '../../redux/slices/automixSlice';
import { playlistCoverUrl, coverAt, DEFAULT_PLAYLIST_COVER } from '../../utils/cover';
import { useDesignV2 } from '../../utils/designV2';
import { formatClock } from '../../utils/automixFormat';
import { track as trackEvent } from '../../utils/analytics';
import AutomixCover from './AutomixCover';

const trackCountOf = (p) => Number(p.track_count ?? p.trackCount ?? p.tracks?.length ?? 0);

// How many set cards fit on one row of the shelf (its grid: minmax(280px, 1fr), 10px gap).
const SET_CARD_MIN = 280;
const SET_GAP = 10;
function useRowCapacity(ref) {
    const [cols, setCols] = useState(4);
    // useLayoutEffect, not useEffect: the first measurement has to happen
    // before the browser paints. With useEffect the shelf painted the guessed
    // 4 cards, then re-rendered with the real capacity a frame later — one
    // card appearing and vanishing every time the page opened.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const measure = () => setCols(Math.max(1, Math.floor((el.clientWidth + SET_GAP) / (SET_CARD_MIN + SET_GAP))));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return cols;
}

const PlaylistCover = ({ playlist }) => {
    const [failed, setFailed] = useState(false);
    const v2 = useDesignV2();
    // v2 tiles are ~170px: ask the CDN for the slot size instead of the 100px thumbnail.
    const src = failed ? DEFAULT_PLAYLIST_COVER : (v2 ? coverAt(playlistCoverUrl(playlist), 400) : playlistCoverUrl(playlist));
    return <img className="amx-card-cover" src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />;
};

const AutomixPicker = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const useServer = useSelector((s) => !!s.auth.accessToken);
    // The sidebar's list paints first; the fetch below replaces it.
    const sidebarPlaylists = useSelector((s) => s.playlist.playlists);
    const sets = useSelector((s) => s.automix.sets);
    const [playlists, setPlaylists] = useState(null);
    // the app's per-tab filter (TabFilterBox): it narrows the sets and the playlists
    const search = useSelector((s) => s.app.filterTerms?.automix || '');
    const [newFiles, setNewFiles] = useState(null);
    // Your sets: one row of the newest, "See all" for the rest. The choice
    // lives in Redux — opening a set unmounts this page, and coming back to a
    // collapsed shelf loses your place in 30 sets.
    const shelfRef = useRef(null);
    const perRow = useRowCapacity(shelfRef);
    const allSets = useSelector((s) => s.automix.setsExpanded);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                if (useServer) {
                    const result = await refreshAndRetry((token) => window.electron.library.getPlaylists(token));
                    // {error} = a failed fetch, not an empty account: keep what is on screen
                    if (alive && Array.isArray(result?.data)) setPlaylists(result.data);
                    else if (alive) setPlaylists((cur) => cur || []);
                } else {
                    const all = await window.electron.db.playlists.getAll(false);
                    if (alive) setPlaylists(all || []);
                }
            } catch (e) {
                if (alive) setPlaylists((cur) => cur || []);
            }
        })();
        return () => { alive = false; };
    }, [useServer]);

    const list = playlists || (sidebarPlaylists?.length ? sidebarPlaylists : null);
    const loading = list === null;
    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = (list || []).filter((p) => !q || (p.name || '').toLowerCase().includes(q));
        // mixable playlists first (the list's own order within each group):
        // DJ-app imports leave plenty of empty folders-turned-playlists behind
        return [...matches.filter((p) => trackCountOf(p) >= 2), ...matches.filter((p) => trackCountOf(p) < 2)];
    }, [list, search]);

    const q = search.trim();
    const shownSets = useMemo(() => {
        const ql = q.toLowerCase();
        return sets.filter((s) => !ql || (s.name || '').toLowerCase().includes(ql));
    }, [sets, q]);
    const visibleSets = allSets ? shownSets : shownSets.slice(0, perRow);

    const toggleAllSets = () => {
        if (!allSets) trackEvent('automix_sets_expanded', { set_count: shownSets.length, filtered: !!q });
        dispatch(setSetsExpanded(!allSets));
    };

    // Nothing here matches: hand the words to the app-wide search (it covers
    // tracks and artists in the library, which this page doesn't)
    const searchLibrary = () => {
        trackEvent('automix_library_search_opened', { from: 'filter_empty', query_length: q.length });
        window.dispatchEvent(new CustomEvent('navbar-search-set', { detail: { query: q } }));
    };

    // the filter sits at the top of whichever section comes first
    const filter = (
        <div className="amx-filter">
            <TabFilterBox placeholder={sets.length ? 'Filter sets and playlists' : 'Filter playlists'} fullWidth tabKey="automix" />
        </div>
    );
    // The sets section (and so the filter in its head) stays put while you type,
    // even when no set matches — moving the field would drop the focus mid-word.
    const setsFirst = sets.length > 0;

    const pick = useCallback(async (playlist, kind, from) => {
        try {
            await dispatch(selectAutomixSource({ kind, id: playlist.id, name: playlist.name, cover: playlistCoverUrl(playlist) }, from));
        } catch (e) {
            toast.error('Couldn’t load that playlist');
        }
    }, [dispatch, toast]);

    const handleNewPlaylist = async () => {
        const res = await window.electron.cuePoints.openDialog('files');
        if (res.canceled || !res.paths?.length) return;
        const files = await window.electron.cuePoints.resolveAudioFiles(res.paths);
        if (!files?.length) { toast.warning('No audio files in that selection'); return; }
        setNewFiles(files);
    };

    return (
        <div className="amx-picker">
            <header className="amx-hero">
                <div className="amx-hero-mark"><AutomixIcon aria-hidden="true" focusable="false" /></div>
                <div className="amx-hero-text">
                    <h1>Automix</h1>
                    <p>
                        Turn a playlist into one continuous DJ set. MixGoat picks the order, matches the tempo and
                        blends each track into the next on the phrase. Play it, export it, or see how it was mixed.
                    </p>
                </div>
            </header>

            {setsFirst && (
                <section className="amx-section">
                    <div className="amx-section-head">
                        <h2>Your sets</h2>
                        {shownSets.length > perRow && (
                            <button type="button" className="amx-link is-quiet" onClick={toggleAllSets}>
                                {allSets ? 'Show less' : `See all ${shownSets.length}`}
                            </button>
                        )}
                        <span className="amx-head-sp" />
                        {filter}
                    </div>
                    {!shownSets.length && <p className="amx-empty-note">No set matches “{q}”.</p>}
                    <div className="amx-shelf" ref={shelfRef}>
                        {visibleSets.map((s) => (
                            <button type="button" key={s.dir} className="amx-set-card" onClick={() => dispatch(openAutomixSet(s.dir, 'shelf'))}>
                                <AutomixCover set={s} size={56} />
                                <span className="amx-set-card-text">
                                    <span className="amx-card-name" title={s.name}>{s.name}</span>
                                    <span className="amx-card-meta">
                                        {formatClock(s.durationSec)} · {s.trackCount} tracks
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            <section className="amx-section">
                <div className="amx-section-head">
                    <h2>Start from a playlist</h2>
                    {!setsFirst && <><span className="amx-head-sp" />{filter}</>}
                </div>

                <div className="amx-grid">
                    <button type="button" className="amx-card amx-card-new" onClick={handleNewPlaylist}>
                        <span className="amx-card-cover amx-new-cover" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                        </span>
                        <span className="amx-card-name">New playlist</span>
                        <span className="amx-card-meta">Choose tracks from your computer</span>
                    </button>

                    {loading && Array.from({ length: 7 }).map((_, i) => (
                        <div key={`s${i}`} className="amx-card amx-card-skel" aria-hidden="true">
                            <span className="amx-card-cover" />
                            <span className="amx-skel-line" />
                            <span className="amx-skel-line short" />
                        </div>
                    ))}

                    {!loading && shown.map((p) => {
                        const count = trackCountOf(p);
                        const tooFew = count < 2;
                        return (
                            <button
                                type="button"
                                key={p.id}
                                className={`amx-card${tooFew ? ' is-dim' : ''}`}
                                onClick={() => (tooFew
                                    ? toast.info('A set needs at least two tracks — add more to this playlist first')
                                    : pick(p, useServer ? 'cloud' : 'local', 'picker'))}
                            >
                                <PlaylistCover playlist={p} />
                                <span className="amx-card-name" title={p.name}>{p.name}</span>
                                <span className="amx-card-meta">{count} {count === 1 ? 'track' : 'tracks'}</span>
                            </button>
                        );
                    })}
                </div>

                {!loading && !shown.length && (
                    <p className="amx-empty-note">
                        {q ? (
                            <>
                                No playlist matches “{q}”.{' '}
                                <button type="button" className="amx-link" onClick={searchLibrary}>Search your library for “{q}”</button>
                            </>
                        ) : 'You don’t have a playlist yet — make one from the tracks on your computer.'}
                    </p>
                )}
            </section>

            <AddToPlaylistModal
                show={!!newFiles}
                onHide={() => setNewFiles(null)}
                bulkFilePaths={newFiles || []}
                bulkLabel={newFiles ? `${newFiles.length} ${newFiles.length === 1 ? 'track' : 'tracks'}` : ''}
                initialTab="create"
                onCreated={async (playlistId, name) => {
                    setNewFiles(null);
                    // Bulk mode writes to the LOCAL library either way (a new
                    // playlist, or the files added to an existing one — that
                    // path reports no name); it syncs to the cloud on its own.
                    let playlistName = name;
                    if (!playlistName) {
                        try { playlistName = (await window.electron.db.playlists.get(playlistId, false))?.name; } catch (e) { /* named below */ }
                    }
                    pick({ id: playlistId, name: playlistName || 'Playlist' }, 'local', 'created');
                }}
            />
        </div>
    );
};

export default AutomixPicker;
